import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import {
  iExhibitorCandidate,
  iTicketsPage,
} from 'services/Ticketing/Ticketing.type';
import {
  card,
  CATEGORY_LABELS,
  CategoryChip,
  formatDateTime,
  formatMoney,
  openBlob,
  PAGE_SX,
  primaryButton,
  TicketingNav,
} from './common';
import { countryName, flag } from './countries';

const BATCH = 50;
const STATUS: Record<string, { label: string; color: string }> = {
  valid: { label: 'Valide', color: '#15803d' },
  pending: { label: 'Non payé', color: '#b45309' },
  cancelled: { label: 'Annulé', color: '#b91c1c' },
};

/** Tickets of an event: search, invitations, exhibitors' badges from EM, export. */
export default function TicketingTickets(): JSX.Element {
  const { eventId = '' } = useParams();
  const { ticketingRepository } = useApiContext();
  const [data, setData] = useState<iTicketsPage | null>(null);
  const [filters, setFilters] = useState({
    q: '',
    type: '',
    status: 'valid',
    page: 1,
  });
  const [q, setQ] = useState('');
  const [panel, setPanel] = useState<'none' | 'one' | 'many' | 'exhibitors'>(
    'none'
  );
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  const reload = (): void => {
    ticketingRepository
      .getTickets(eventId, filters)
      .then(setData)
      .catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  };
  useEffect(reload, [eventId, filters]);

  if (!data)
    return (
      <Box sx={PAGE_SX}>
        {message ? (
          <Alert severity="error">{message.text}</Alert>
        ) : (
          'Chargement…'
        )}
      </Box>
    );

  const act = (p: Promise<unknown>, ok: string): void => {
    p.then(() => {
      setMessage({ type: 'success', text: ok });
      reload();
    }).catch((e: Error) => setMessage({ type: 'error', text: e.message }));
  };
  const activeTypes = data.types.filter((t) => t.is_active);

  return (
    <Box sx={PAGE_SX}>
      <TicketingNav
        current="tickets"
        canConfigure={data.can_edit}
        tickets={data.event.tickets}
        eventName={data.event.name}
      />
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFilters({ ...filters, q, page: 1 });
          }}
          style={{ flex: 1, minWidth: 220 }}
        >
          <TextField
            size="small"
            fullWidth
            label="Rechercher (nom, société, e-mail, code)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </form>
        <Select
          size="small"
          displayEmpty
          value={filters.type}
          onChange={(e) =>
            setFilters({ ...filters, type: String(e.target.value), page: 1 })
          }
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tous les billets</MenuItem>
          {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
            <MenuItem key={k} value={k}>
              {l} (catégorie)
            </MenuItem>
          ))}
          {data.types.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={filters.status}
          onChange={(e) =>
            setFilters({ ...filters, status: String(e.target.value), page: 1 })
          }
        >
          <MenuItem value="valid">Valides</MenuItem>
          <MenuItem value="cancelled">Annulés</MenuItem>
          <MenuItem value="pending">Non payés</MenuItem>
          <MenuItem value="all">Tous</MenuItem>
        </Select>
        <Button
          variant="outlined"
          sx={{ textTransform: 'none', borderColor: '#ddd', color: '#111' }}
          onClick={async () =>
            await ticketingRepository
              .exportCsv(eventId, {
                q: filters.q,
                type: filters.type,
                status: filters.status,
              })
              .then((b) => openBlob(b, `billets-${eventId}.csv`))
              .catch((e: Error) =>
                setMessage({ type: 'error', text: e.message })
              )
          }
        >
          Export CSV
        </Button>
        {data.can_edit && (
          <>
            <Button
              variant="contained"
              sx={primaryButton}
              onClick={() => setPanel(panel === 'one' ? 'none' : 'one')}
            >
              Créer un billet
            </Button>
            <Button
              variant="outlined"
              sx={{ textTransform: 'none', borderColor: '#ddd', color: '#111' }}
              onClick={() =>
                setPanel(panel === 'exhibitors' ? 'none' : 'exhibitors')
              }
            >
              Badges exposants
            </Button>
            <Button
              variant="outlined"
              sx={{ textTransform: 'none', borderColor: '#ddd', color: '#111' }}
              onClick={() => setPanel(panel === 'many' ? 'none' : 'many')}
            >
              Import en masse
            </Button>
          </>
        )}
      </Box>

      {data.can_edit && panel !== 'none' && activeTypes.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Créez d&apos;abord un type de billet dans « Configuration ».
        </Alert>
      )}
      {panel === 'one' && activeTypes.length > 0 && (
        <InviteForm
          eventId={eventId}
          types={activeTypes}
          onDone={(text) => {
            setPanel('none');
            setMessage({ type: 'success', text });
            reload();
          }}
          onError={(text) => setMessage({ type: 'error', text })}
        />
      )}
      {panel === 'many' && activeTypes.length > 0 && (
        <ImportForm
          eventId={eventId}
          types={activeTypes}
          onDone={(text) => {
            setPanel('none');
            setMessage({ type: 'success', text });
            reload();
          }}
        />
      )}
      {panel === 'exhibitors' && activeTypes.length > 0 && (
        <ExhibitorBadges
          eventId={eventId}
          types={activeTypes}
          onDone={(text) => {
            setPanel('none');
            setMessage({ type: 'success', text });
            reload();
          }}
        />
      )}

      <Typography sx={{ fontSize: 14, color: '#666', mb: 1 }}>
        {data.total} billet(s)
      </Typography>
      <Box sx={{ ...card, p: 0, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Personne</TableCell>
              <TableCell>Billet</TableCell>
              <TableCell>Pays / nationalité</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Entrée</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.tickets.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                  sx={{ color: '#888', py: 4 }}
                >
                  Aucun billet.
                </TableCell>
              </TableRow>
            )}
            {data.tickets.map((t) => (
              <TableRow key={t.id} sx={{ verticalAlign: 'top' }}>
                <TableCell>
                  <b>{t.name}</b>
                  <Typography sx={{ fontSize: 13, color: '#555' }}>
                    {[t.company, t.job_title].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {t.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <CategoryChip category={t.category} />
                  <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                    {t.type}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: '#888',
                      fontFamily: 'monospace',
                    }}
                  >
                    {t.code}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>
                    {t.source === 'invitation'
                      ? 'Invitation'
                      : t.paid_cents
                      ? formatMoney(t.paid_cents)
                      : 'Gratuit'}{' '}
                    · {formatDateTime(t.created_at)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {t.country && (
                    <div>
                      {flag(t.country)} {countryName(t.country)}
                    </div>
                  )}
                  {t.nationality && (
                    <div style={{ color: '#888', fontSize: 12 }}>
                      Nat. {flag(t.nationality)} {countryName(t.nationality)}
                    </div>
                  )}
                </TableCell>
                <TableCell
                  sx={{ color: STATUS[t.status]?.color, fontSize: 13 }}
                >
                  {STATUS[t.status]?.label}
                  {t.status === 'valid' && (
                    <div style={{ color: '#888', fontSize: 12 }}>
                      {t.emailed_at
                        ? `Envoyé ${formatDateTime(t.emailed_at)}`
                        : 'Pas encore envoyé'}
                    </div>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {t.first_scanned_at ? (
                    <>
                      {formatDateTime(t.first_scanned_at)}
                      <div style={{ color: '#888', fontSize: 12 }}>
                        {t.entries} passage(s)
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t.download_url && (
                    <Button
                      size="small"
                      sx={{ textTransform: 'none' }}
                      href={t.download_url}
                      target="_blank"
                    >
                      PDF
                    </Button>
                  )}
                  {t.status === 'valid' && (
                    <Button
                      size="small"
                      sx={{ textTransform: 'none' }}
                      onClick={() =>
                        act(
                          ticketingRepository.resend(eventId, t.id),
                          `Billet renvoyé à ${t.email}.`
                        )
                      }
                    >
                      Renvoyer
                    </Button>
                  )}
                  {data.can_edit && t.status === 'valid' && (
                    <Button
                      size="small"
                      color="error"
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Annuler le billet de ${t.name} ? Il sera refusé à l'entrée.`
                          )
                        )
                          act(
                            ticketingRepository.updateTicket(eventId, t.id, {
                              action: 'cancel',
                            }),
                            'Billet annulé.'
                          );
                      }}
                    >
                      Annuler
                    </Button>
                  )}
                  {data.can_edit && t.status === 'cancelled' && (
                    <Button
                      size="small"
                      sx={{ textTransform: 'none' }}
                      onClick={() =>
                        act(
                          ticketingRepository.updateTicket(eventId, t.id, {
                            action: 'restore',
                          }),
                          'Billet rétabli.'
                        )
                      }
                    >
                      Rétablir
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {data.last_page > 1 && (
        <Pagination
          sx={{ mt: 2 }}
          count={data.last_page}
          page={data.current_page}
          onChange={(_, page) => setFilters({ ...filters, page })}
        />
      )}
    </Box>
  );
}

type Types = iTicketsPage['types'];

function TypeSelect({
  types,
  value,
  onChange,
}: {
  types: Types;
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <Select
      size="small"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      sx={{ minWidth: 260 }}
    >
      {types.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {CATEGORY_LABELS[t.category]} · {t.name}
          {t.is_public ? '' : ' (invitation)'}
        </MenuItem>
      ))}
    </Select>
  );
}

function InviteForm({
  eventId,
  types,
  onDone,
  onError,
}: {
  eventId: string;
  types: Types;
  onDone: (t: string) => void;
  onError: (t: string) => void;
}): JSX.Element {
  const { ticketingRepository } = useApiContext();
  const [v, setV] = useState({
    ticket_type_id: (types.find((t) => !t.is_public) ?? types[0]).id,
    firstname: '',
    lastname: '',
    email: '',
    company: '',
    job_title: '',
    send: true,
  });
  const fields: Array<[keyof typeof v, string]> = [
    ['firstname', 'Prénom'],
    ['lastname', 'Nom'],
    ['email', 'E-mail'],
    ['company', 'Société'],
    ['job_title', 'Fonction'],
  ];
  return (
    <Box
      component="form"
      sx={{
        ...card,
        mb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
      }}
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        ticketingRepository
          .invite(eventId, v)
          .then((r) =>
            onDone(
              `Billet créé pour ${r.name}${
                v.send ? ' et envoyé par e-mail' : ''
              }.`
            )
          )
          .catch((er: Error) => onError(er.message));
      }}
    >
      <TypeSelect
        types={types}
        value={v.ticket_type_id}
        onChange={(id) => setV({ ...v, ticket_type_id: id })}
      />
      {fields.map(([k, label]) => (
        <TextField
          key={k}
          size="small"
          label={label}
          required={['firstname', 'lastname', 'email'].includes(k)}
          type={k === 'email' ? 'email' : 'text'}
          value={v[k] as string}
          onChange={(e) => setV({ ...v, [k]: e.target.value })}
        />
      ))}
      <FormControlLabel
        control={
          <Checkbox
            checked={v.send}
            onChange={(e) => setV({ ...v, send: e.target.checked })}
          />
        }
        label="Envoyer par e-mail"
      />
      <Button type="submit" variant="contained" sx={primaryButton}>
        Créer le billet
      </Button>
      <Typography sx={{ fontSize: 12, color: '#888', width: '100%' }}>
        Billet offert, valable immédiatement, quel que soit son prix.
      </Typography>
    </Box>
  );
}

/** Sends rows in batches of 50, with a progress bar (no long request on the server). */
async function sendInBatches(
  send: (
    rows: Array<Record<string, unknown>>
  ) => Promise<{ created: number; skipped: Array<number | string> }>,
  rows: Array<Record<string, unknown>>,
  onProgress: (done: number) => void
): Promise<{ created: number; skipped: Array<number | string> }> {
  let created = 0;
  const skipped: Array<number | string> = [];
  for (let i = 0; i < rows.length; i += BATCH) {
    const r = await send(rows.slice(i, i + BATCH));
    created += r.created;
    skipped.push(...r.skipped);
    onProgress(Math.min(rows.length, i + BATCH));
  }
  return { created, skipped };
}

function ImportForm({
  eventId,
  types,
  onDone,
}: {
  eventId: string;
  types: Types;
  onDone: (t: string) => void;
}): JSX.Element {
  const { ticketingRepository } = useApiContext();
  const [typeId, setTypeId] = useState(
    (types.find((t) => t.category === 'exhibitor') ?? types[0]).id
  );
  const [text, setText] = useState('');
  const [send, setSend] = useState(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = text
    .split('\n')
    .map((line, i) => ({
      line: i + 1,
      cols: line.split(/\t|;|,/).map((c) => c.trim()),
    }))
    .filter(
      (r) =>
        r.cols.join('') !== '' &&
        !(
          r.line === 1 &&
          /mail/i.test(r.cols[2] ?? '') &&
          !(r.cols[2] ?? '').includes('@')
        )
    )
    .map((r) => ({
      line: r.line,
      firstname: r.cols[0],
      lastname: r.cols[1],
      email: r.cols[2],
      company: r.cols[3],
    }));

  const run = (): void => {
    setError(null);
    setProgress(0);
    sendInBatches(
      async (batch) =>
        await ticketingRepository.inviteBatch(eventId, {
          ticket_type_id: typeId,
          send,
          rows: batch,
        }),
      rows,
      setProgress
    )
      .then((r) =>
        onDone(
          `${r.created} billet(s) créé(s).${
            r.skipped.length
              ? ` Ignoré(s) (prénom, nom ou e-mail manquant) : ligne(s) ${r.skipped
                  .slice(0, 20)
                  .join(', ')}.`
              : ''
          }`
        )
      )
      .catch((e: Error) => {
        setError(e.message);
        setProgress(null);
      });
  };

  return (
    <Box
      sx={{
        ...card,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <TypeSelect types={types} value={typeId} onChange={setTypeId} />
      <TextField
        multiline
        minRows={6}
        size="small"
        label="Une personne par ligne : Prénom ; Nom ; E-mail ; Société"
        value={text}
        onChange={(e) => setText(e.target.value)}
        helperText="Copiez-collez depuis Excel (colonnes séparées par tabulation, point-virgule ou virgule)."
        InputProps={{ sx: { fontFamily: 'monospace' } }}
      />
      {progress !== null && (
        <LinearProgress
          variant="determinate"
          value={rows.length ? (progress / rows.length) * 100 : 0}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={send}
              onChange={(e) => setSend(e.target.checked)}
            />
          }
          label="Envoyer les billets par e-mail"
        />
        <Button
          variant="contained"
          disabled={!rows.length || progress !== null}
          sx={primaryButton}
          onClick={run}
        >
          Créer {rows.length || ''} billet(s)
        </Button>
      </Box>
    </Box>
  );
}

/** Badges for the exhibitors registered to the event in EM. */
function ExhibitorBadges({
  eventId,
  types,
  onDone,
}: {
  eventId: string;
  types: Types;
  onDone: (t: string) => void;
}): JSX.Element {
  const { ticketingRepository } = useApiContext();
  const [typeId, setTypeId] = useState(
    (types.find((t) => t.category === 'exhibitor') ?? types[0]).id
  );
  const [list, setList] = useState<iExhibitorCandidate[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [send, setSend] = useState(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setList(null);
    ticketingRepository
      .getExhibitors(eventId, typeId)
      .then((r) => {
        setList(r.exhibitors);
        setSelected(
          r.exhibitors
            .filter((x) => !x.has_badge && x.email)
            .map((x) => x.exhibitor_event_id)
        );
      })
      .catch((e: Error) => setError(e.message));
  }, [typeId]);

  const rows = (list ?? [])
    .filter((x) => selected.includes(x.exhibitor_event_id))
    .map((x) => ({
      exhibitor_event_id: x.exhibitor_event_id,
      firstname: x.firstname,
      lastname: x.lastname,
      email: x.email,
      company: x.company,
      line: x.company,
    }));

  return (
    <Box
      sx={{
        ...card,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontSize: 14, color: '#555' }}>
        Un badge par exposant inscrit au salon dans EM, au nom de son contact.
        Les exposants qui ont déjà ce badge ne sont pas recréés.
      </Typography>
      <TypeSelect types={types} value={typeId} onChange={setTypeId} />
      {!list ? (
        <Typography sx={{ fontSize: 14 }}>Chargement des exposants…</Typography>
      ) : list.length === 0 ? (
        <Typography sx={{ fontSize: 14 }}>
          Aucun exposant sur ce salon.
        </Typography>
      ) : (
        <Box
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            border: '1px solid #eee',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selected.length > 0 &&
                      selected.length ===
                        list.filter((x) => !x.has_badge && x.email).length
                    }
                    onChange={(e) =>
                      setSelected(
                        e.target.checked
                          ? list
                              .filter((x) => !x.has_badge && x.email)
                              .map((x) => x.exhibitor_event_id)
                          : []
                      )
                    }
                  />
                </TableCell>
                <TableCell>Société</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((x) => (
                <TableRow
                  key={x.exhibitor_event_id}
                  sx={{ opacity: x.has_badge ? 0.5 : 1 }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      disabled={x.has_badge || !x.email}
                      checked={selected.includes(x.exhibitor_event_id)}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? [...selected, x.exhibitor_event_id]
                            : selected.filter(
                                (id) => id !== x.exhibitor_event_id
                              )
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>{x.company}</TableCell>
                  <TableCell>
                    {[x.firstname, x.lastname].filter(Boolean).join(' ')}
                  </TableCell>
                  <TableCell>
                    {x.email ?? (
                      <span style={{ color: '#b91c1c' }}>sans e-mail</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#15803d' }}>
                    {x.has_badge ? 'Badge déjà émis' : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {progress !== null && (
        <LinearProgress
          variant="determinate"
          value={rows.length ? (progress / rows.length) * 100 : 0}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={send}
              onChange={(e) => setSend(e.target.checked)}
            />
          }
          label="Envoyer les badges par e-mail"
        />
        <Button
          variant="contained"
          disabled={!rows.length || progress !== null}
          sx={primaryButton}
          onClick={() => {
            setProgress(0);
            sendInBatches(
              async (batch) =>
                await ticketingRepository.inviteBatch(eventId, {
                  ticket_type_id: typeId,
                  send,
                  rows: batch,
                }),
              rows,
              setProgress
            )
              .then((r) =>
                onDone(
                  `${r.created} badge(s) exposant créé(s).${
                    r.skipped.length
                      ? ` ${r.skipped.length} ignoré(s) (contact incomplet ou badge existant).`
                      : ''
                  }`
                )
              )
              .catch((e: Error) => {
                setError(e.message);
                setProgress(null);
              });
          }}
        >
          Créer {rows.length || ''} badge(s)
        </Button>
      </Box>
    </Box>
  );
}
